import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { MenuTemplateEntry } from '../shared/menu/menu-route.model';

// ── Request/Response interfaces ──────────────────────────────────────

export interface ModuleMenuTemplate {
  moduleId: number;
  roots: MenuTemplateEntry[];
}

export interface ProvisionCustomerRequest {
  domainName: string;
  databaseName: string;
  sqlServerType: 'AzureSql' | 'OnPremise';
  sqlServerAddress?: string;
  sqlAdminLogin?: string;
  sqlAdminPassword?: string;

  // Customer (new or existing)
  existingUserAccountID?: number;
  newCustomerName?: string;
  newCustomerAddress?: string;
  newCustomerZip?: string;
  newCustomerCity?: string;
  newCustomerEmail?: string;
  newCustomerPhone?: string;

  // Admin user for the new tenant
  adminUsername: string;
  adminPassword: string;
  adminFullName: string;
  adminEmail?: string;

  // Branding
  domainColors?: string;
  userLogo?: string;
  userBackground?: string;
  userLogoSmall?: string;

  // Features
  useExternalWorkOrders: boolean;
  useStockIntegration: boolean;
  useBugReport: boolean;
  activeModuleIDs: number[];

  // Default menu templates per module, bundled by the wizard from the
  // frontend route manifests. Applied on the new tenant's connection
  // inside the provisioning transaction, after the baseline menu seed.
  menuTemplates?: ModuleMenuTemplate[];
}

export interface ProvisionCustomerResult {
  success: boolean;
  domainID: number;
  userAccountID: number;
  databaseName: string;
  domainName: string;
  apiKey: string;
  sqlLoginName: string;
  sqlPassword?: string;
  adminUserId: number;
  provisionStatus: string;
  steps: string[];
  warnings: string[];
}

export interface ValidateNameRequest {
  name: string;
}

// ── Service ──────────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root'
})
export class TenantProvisioningService {

  private get apiUrl(): string {
    return `${this.configService.get('apiBaseUrl')}/SystemDomain`;
  }

  constructor(
    private http: HttpClient,
    private configService: ConfigService
  ) { }

  /**
   * Provision a complete new customer tenant (DB + schema + domain + admin user).
   * Returns ProvisionCustomerResult with success=false and steps log on failure.
   */
  provision(request: ProvisionCustomerRequest): Observable<ProvisionCustomerResult> {
    return this.http.post<any>(`${this.apiUrl}/Provision`, request).pipe(
      map(response => {
        if (response?.success && response.result) {
          return response.result;
        }
        // On API-level failure, construct a result from the error information
        return response?.result || {
          success: false,
          domainID: 0,
          userAccountID: 0,
          databaseName: '',
          domainName: '',
          apiKey: '',
          sqlLoginName: '',
          adminUserId: 0,
          provisionStatus: 'Failed',
          steps: [response?.errorMessage?.[0]?.message || 'Unknown error'],
          warnings: []
        } as ProvisionCustomerResult;
      })
    );
  }

  /**
   * Validate that a database name is available and correctly formatted.
   * Returns true if available, false otherwise.
   */
  validateDbName(name: string): Observable<boolean> {
    return this.http.post<any>(`${this.apiUrl}/Provision/ValidateDbName`, { name }).pipe(
      map(response => response?.success ? response.result : false)
    );
  }

  /**
   * Validate that a domain name is available and correctly formatted.
   * Returns true if available, false otherwise.
   */
  validateDomainName(name: string): Observable<boolean> {
    return this.http.post<any>(`${this.apiUrl}/Provision/ValidateDomainName`, { name }).pipe(
      map(response => response?.success ? response.result : false)
    );
  }
}
